import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Chip,
  Modal,
  MenuItem,
  Select,
  AppBar,
  Toolbar,
  Paper,
  TextField,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import { useNavigate, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const EditTeacherHomework = () => {
  const { id } = useParams();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [homeworkDate, setHomeworkDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [caption, setCaption] = useState('');
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);

  const navigate = useNavigate();
  const drawerWidth = 240;

  // Fetch Homework Data
  const fetchHomeworkData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Homework/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        setClassId(responseData.classId);
        setSectionId(responseData.sectionId);
        setHomeworkDate(new Date(responseData.homeworkDate).toISOString().slice(0, 16));
        setDueDate(new Date(responseData.dueDate).toISOString().slice(0, 16));
        setCaption(responseData.caption);
        setExistingFiles(responseData.files || []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch homework data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Class Data
  const fetchClassData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const teacherId = sessionStorage.getItem('employeeId');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Exam/GetClass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ teacherId: parseInt(teacherId, 10) }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setClassData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch class data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setClassData([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Section Data
  const fetchSectionData = async (selectedClassId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/AssignmentCreate/ddlSection_clsId`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classId: selectedClassId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setSectionData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch section data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSectionData([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Fetch Homework, Class, and Section Data on Component Mount
  useEffect(() => {
    fetchHomeworkData();
    fetchClassData();
  }, []);

  // Fetch Section Data when Class is Selected
  useEffect(() => {
    if (classId) {
      fetchSectionData(classId);
    }
  }, [classId]);

  const handleFileChange = (e) => {
    setFiles([...files, ...e.target.files]);
  };

  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const removeExistingFile = (index) => {
    const updatedFiles = [...existingFiles];
    updatedFiles.splice(index, 1);
    setExistingFiles(updatedFiles);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!classId || !sectionId || !homeworkDate || !dueDate || !caption) {
      setSnackbarMessage('Please fill all fields.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      return;
    }

    const formData = new FormData();
    formData.append('ClassId', classId);
    formData.append('SectionId', sectionId);
    formData.append('HomeworkDate', new Date(homeworkDate).toISOString());
    formData.append('DueDate', new Date(dueDate).toISOString());
    formData.append('Caption', caption);
    files.forEach((file) => {
      formData.append('files', file);
    });

    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Homework/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        setLoadingBarProgress(100);
        setShowSuccessModal(true);
      } else {
        const errorData = await response.json();
        setSnackbarMessage(errorData.message || 'Failed to update homework.');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: 3,
        backgroundColor: '#f4f6f9',
        minHeight: '100vh',
      }}
    >
      <LoadingBar
        progress={loadingBarProgress}
        onLoaderFinished={() => setLoadingBarProgress(0)}
        color="rgb(152, 106, 182)"
        height={5}
      />

      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
          mb: 1,
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
            Edit Homework
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        sx={{
          padding: 4,
          width: '100%',
          margin: 'auto',
          backgroundColor: '#fafafa',
        }}
      >
        <form onSubmit={handleSave} style={{ width: '100%' }}>
          {/* Dropdowns Section */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: '#2c3e50',
                fontSize: '1.1rem',
              }}
            >
              Select Details
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2.5,
              }}
            >
              {/* Class Dropdown */}
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="class">Class</InputLabel>
                <Select
                  id="class"
                  value={classId}
                  label="Class"
                  onChange={(e) => setClassId(e.target.value)}
                  sx={{ backgroundColor: '#fff' }}
                >
                  {classData.map((classItem) => (
                    <MenuItem key={classItem.classId} value={classItem.classId}>
                      {classItem.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Section Dropdown */}
              <FormControl fullWidth variant="outlined">
                <InputLabel htmlFor="section">Section</InputLabel>
                <Select
                  id="section"
                  value={sectionId}
                  label="Section"
                  onChange={(e) => setSectionId(e.target.value)}
                  disabled={!classId}
                  sx={{ backgroundColor: '#fff' }}
                >
                  {sectionData.map((sectionItem) => (
                    <MenuItem key={sectionItem.sectionId} value={sectionItem.sectionId}>
                      {sectionItem.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Homework Details Section */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 2,
              p: 3,
              mb: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: '#2c3e50',
                fontSize: '1.1rem',
              }}
            >
              Homework Details
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2.5,
              }}
            >
              <TextField
                fullWidth
                label="Homework Date"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={homeworkDate}
                onChange={(e) => setHomeworkDate(e.target.value)}
                sx={{ backgroundColor: '#fff' }}
              />
              <TextField
                fullWidth
                label="Due Date"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                sx={{ backgroundColor: '#fff' }}
              />
            </Box>

            <TextField
              fullWidth
              label="Caption"
              multiline
              rows={3}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              sx={{ mt: 2, backgroundColor: '#fff' }}
            />

            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
              sx={{
                mt: 2,
                backgroundColor: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                '&:hover': {
                  backgroundColor: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                },
              }}
            >
              Upload Files
              <VisuallyHiddenInput
                type="file"
                multiple
                onChange={handleFileChange}
              />
            </Button>

            {/* Existing Files */}
            {existingFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057' }}>
                  Existing Files:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {existingFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.originalFileName}
                      onDelete={() => removeExistingFile(index)}
                      deleteIcon={<DeleteIcon fontSize="small" />}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        '& .MuiChip-deleteIcon': {
                          color: '#dc3545',
                          '&:hover': {
                            color: '#c82333',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* New Files */}
            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#495057' }}>
                  New Files:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {files.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      onDelete={() => removeFile(index)}
                      deleteIcon={<DeleteIcon fontSize="small" />}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        '& .MuiChip-deleteIcon': {
                          color: '#dc3545',
                          '&:hover': {
                            color: '#c82333',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
              mt: 4,
              p: 3,
              backgroundColor: '#ffffff',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Tooltip
              title={
                !classId
                  ? 'Please select a class'
                  : !sectionId
                  ? 'Please select a section'
                  : !homeworkDate
                  ? 'Please select homework date'
                  : !dueDate
                  ? 'Please select due date'
                  : !caption
                  ? 'Please enter a caption'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!classId || !sectionId || !homeworkDate || !dueDate || !caption}
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                    letterSpacing: '0.5px',
                    backgroundColor: '#28a745',
                    minWidth: '140px',
                    '&:hover': {
                      backgroundColor: '#218838',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                    },
                    transition: 'all 0.3s ease',
                    '&:disabled': {
                      backgroundColor: '#cccccc',
                    },
                  }}
                >
                  SAVE
                </Button>
              </span>
            </Tooltip>

            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 600,
                fontSize: '1rem',
                letterSpacing: '0.5px',
                borderColor: '#dc3545',
                color: '#dc3545',
                minWidth: '140px',
                '&:hover': {
                  borderColor: '#c82333',
                  backgroundColor: '#fff5f5',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              CANCEL
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)', padding: '12px 16px' }}>
            <Typography
              id="success-modal-title"
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              Homework Updated <Typography sx={{ color: '#4caf50', fontSize: '1.2rem' }}>✓</Typography>
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography
              id="success-modal-description"
              sx={{
                mb: 2,
                fontSize: '1rem',
                color: '#495057',
                textAlign: 'center',
                lineHeight: 1.6,
              }}
            >
              Homework has been updated successfully!
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                sx={{
                  backgroundColor: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                  '&:hover': {
                    backgroundColor: 'linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)',
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 500,
            fontSize: '0.95rem',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditTeacherHomework;
